import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { keys } from 'lodash'
import classnames from 'classnames/bind'
import {
  colorVars,
  colorLongToShort,
  getCssColorFromColorName,
  INHERIT,
} from '../../../utils/colors'
import Icon from '../icon'
import Text from '../text'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const COLOR_NAMES = keys(colorVars).map(colorLongToShort)
const COLOR_OPTIONS = COLOR_NAMES.concat(INHERIT, '')
const ICON_STYLES = ['solid', 'regular', 'light', 'brand']

const Notification = ({
  isVisible,
  onClose,
  iconName,
  iconStyle = 'light',
  color,
  elmId = 'application_notification_toast',
  message,
  title,
  isAnimated,
  fluid,
  onCloseButton,
  rawHtml,
  timestamp,
  timestampRefreshInMS,
  children,
  contentOnly = false,
}) => {
  const extraProps = {}
  if (!onCloseButton && onClose) {
    extraProps.onClick = onClose
  }

  const MessageContent = rawHtml
    ? () => <div dangerouslySetInnerHTML={{ __html: rawHtml }} />
    : () => (
        <>
          <Text data-elm-id='header' color={color} weight='heavy'>
            {title}
          </Text>
          <Text data-elm-id='content'>{message}</Text>
        </>
      )
  const initTimestamp = timestamp ? moment(timestamp).fromNow() : ''
  const [elapsedTime, setElapsedTime] = useState(initTimestamp)

  useEffect(() => {
    let timeoutId
    function updateElapsedTime() {
      clearTimeout(timeoutId)
      setElapsedTime(moment(timestamp).fromNow())
      timeoutId = setTimeout(updateElapsedTime, timestampRefreshInMS)
    }

    if (timestamp) {
      timeoutId = setTimeout(updateElapsedTime, timestampRefreshInMS)
    }

    return () => {
      clearTimeout(timeoutId)
    }
  }, [])

  return (
    <div
      data-elm-id={elmId}
      className={cx(
        { hide: !isVisible },
        'container',
        'u-mb-1',
        { 'content-only': contentOnly },
        !onCloseButton && onClose ? 'u-pointer-cursor' : '',
        isAnimated ? 'animate' : 'sticky',
        fluid ? 'fluid' : ''
      )}
      style={{ borderColor: getCssColorFromColorName(color) }}
      {...extraProps}
    >
      {iconName && (
        <div className={cx('icon')}>
          <Icon
            name={iconName}
            color={color}
            iconStyle={iconStyle}
            size='big'
          />
        </div>
      )}
      <div className={cx('content')}>
        <MessageContent />
        {children}
        {timestamp && (
          <Text size='mini' color='grayMedium'>
            {elapsedTime}
          </Text>
        )}
      </div>
      {onCloseButton && (
        <div
          className={cx('close-btn')}
          onPointerDown={event => event.stopPropagation()}
        >
          <Icon
            iconStyle='regular'
            name='xmark'
            size='large'
            onClick={onClose}
            className='u-pointer-cursor'
          />
        </div>
      )}
    </div>
  )
}

Notification.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  iconName: PropTypes.string,
  iconStyle: PropTypes.oneOf(['', ...ICON_STYLES]),
  color: PropTypes.oneOf(COLOR_OPTIONS),
  elmId: PropTypes.string,
  message: PropTypes.node,
  title: PropTypes.string,
  isAnimated: PropTypes.bool,
  fluid: PropTypes.bool,
  onCloseButton: PropTypes.bool,
  rawHtml: PropTypes.string,
  timestamp: PropTypes.string,
  timestampRefreshInMS: PropTypes.number,
  children: PropTypes.node,
  contentOnly: PropTypes.bool,
}

Notification.defaultProps = {
  isAnimated: false,
  fluid: false,
  onCloseButton: false,
  timestampRefreshInMS: 30000,
}

export default Notification
